import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Play, Pause, Square, Trash2, RefreshCw, TrendingUp, Activity, AlertTriangle, X, ArrowLeft, GitCompare } from "lucide-react";
import { BacktestVsLivePanel } from "@/components/deployments/BacktestVsLivePanel";
import { DriftSettingsPanel } from "@/components/deployments/DriftSettingsPanel";

/** Deploy modal — pick connection + confirm risk */
export function DeployModal({ strategy, onClose, onDeployed }) {
  const [connections, setConnections] = useState([]);
  const [cid, setCid] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/broker/connections").then(({data}) => {
      const live = data.filter(c => c.live_trading_enabled);
      setConnections(data);
      if (live[0]) setCid(live[0].connection_id);
      setLoaded(true);
    }).catch(()=>{});
  }, []);

  const deploy = async () => {
    if (!cid) { toast.error("Pick a connection with live trading enabled"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/deployments", { strategy_id: strategy.strategy_id, connection_id: cid });
      toast.success("Deployment started");
      onDeployed(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Deploy failed");
    } finally { setBusy(false); }
  };

  const eligible = connections.filter(c => c.live_trading_enabled);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose} data-testid="deploy-modal">
      <div className="card-sharp p-6 max-w-lg w-full" onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Activity size={18} className="text-[var(--accent)]"/>
            <h3 className="font-display text-xl text-white">Deploy <span className="text-[var(--accent)]">{strategy.name}</span></h3>
          </div>
          <button data-testid="deploy-close" onClick={onClose} className="p-1.5 border border-white/10 rounded hover:border-[var(--loss)] hover:text-[var(--loss)]"><X size={14}/></button>
        </div>

        <div className="text-xs text-[var(--text-secondary)] p-3 border border-[var(--warning)]/30 bg-[var(--warning)]/5 rounded flex items-start gap-2 mb-4">
          <AlertTriangle size={13} className="text-[var(--warning)] mt-0.5 shrink-0"/>
          <span>
            This will run the strategy on your connected broker every 60 seconds. Real orders will be placed. You accept full responsibility.
          </span>
        </div>

        <label className="label-tiny">Broker connection (live trading enabled only)</label>
        {loaded && eligible.length === 0 ? (
          <div className="text-xs font-mono text-[var(--text-muted)] italic border border-dashed border-white/10 rounded p-4">
            No live-enabled connections. Go to <Link to="/broker" className="text-[var(--accent)] underline">/broker</Link> and toggle "Enable LIVE trading" on a connection first.
          </div>
        ) : (
          <div className="space-y-2 max-h-60 overflow-auto" data-testid="deploy-conn-list">
            {eligible.map(c => (
              <label key={c.connection_id}
                className={`border rounded p-3 cursor-pointer flex items-center gap-3 transition-colors
                  ${cid === c.connection_id ? "border-[var(--accent)] bg-[var(--accent)]/5" : "border-white/10 hover:border-white/25"}`}
                data-testid={`deploy-conn-${c.connection_id}`}>
                <input type="radio" checked={cid === c.connection_id} onChange={()=>setCid(c.connection_id)} className="accent-[var(--accent)]"/>
                <div className="flex-1">
                  <div className="text-sm text-white">{c.label}</div>
                  <div className="text-[10px] font-mono text-[var(--text-muted)]">{c.provider} · {c.environment} · {c.account_id}</div>
                </div>
                <span className="chip chip-loss">◆ LIVE</span>
              </label>
            ))}
          </div>
        )}

        <button data-testid="deploy-submit-btn" onClick={deploy} disabled={busy || eligible.length === 0}
          className="btn-primary w-full mt-5 flex items-center justify-center gap-2">
          <Play size={14}/> {busy ? "Deploying…" : "Start deployment"}
        </button>
      </div>
    </div>
  );
}

/** Deployment card for lists */
export function DeploymentCard({ dep, onOpen, onPatch, onDelete }) {
  const statusColor = dep.status === "running" ? "chip-profit" : dep.status === "paused" ? "" : "chip-loss";
  return (
    <div className="card-sharp p-5" data-testid={`deployment-card-${dep.deployment_id}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] flex items-center gap-2">
            <span className={`chip ${statusColor}`} data-testid={`dep-status-${dep.deployment_id}`}>◆ {dep.status.toUpperCase()}</span>
            <span>{dep.provider}</span>
          </div>
          <h3 className="font-display text-lg text-white mt-1 truncate">{dep.label}</h3>
          <div className="text-[11px] font-mono text-[var(--text-secondary)]">
            {dep.symbol} · {(dep.timeframe || "PERIOD_H1").replace("PERIOD_","")} · {dep.connection_label}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-4">
        <MiniStat label="Trades" value={dep.trades_placed || 0}/>
        <MiniStat label="Closed" value={dep.closed_trades || 0}/>
        <MiniStat label="Last run"
          value={dep.last_run_at ? new Date(dep.last_run_at).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'}) : "—"}/>
      </div>
      {dep.last_error && (
        <div className="mt-3 text-[10px] font-mono text-[var(--loss)] truncate" title={dep.last_error}>
          ⚠ {dep.last_error}
        </div>
      )}
      <div className="mt-4 pt-4 border-t border-white/5 flex gap-2">
        <button data-testid={`dep-open-${dep.deployment_id}`} onClick={()=>onOpen(dep)}
          className="btn-outline flex-1 text-xs py-2">Open</button>
        {dep.status === "running" ? (
          <button data-testid={`dep-pause-${dep.deployment_id}`} onClick={()=>onPatch(dep, "paused")}
            className="p-2 border border-white/10 rounded hover:border-[var(--warning)] hover:text-[var(--warning)]"><Pause size={13}/></button>
        ) : dep.status === "paused" ? (
          <button data-testid={`dep-resume-${dep.deployment_id}`} onClick={()=>onPatch(dep, "running")}
            className="p-2 border border-white/10 rounded hover:border-[var(--profit)] hover:text-[var(--profit)]"><Play size={13}/></button>
        ) : null}
        <button data-testid={`dep-stop-${dep.deployment_id}`} onClick={()=>onPatch(dep, "stopped")}
          className="p-2 border border-white/10 rounded hover:border-[var(--loss)] hover:text-[var(--loss)]"><Square size={13}/></button>
        <button data-testid={`dep-delete-${dep.deployment_id}`} onClick={()=>onDelete(dep)}
          className="p-2 border border-white/10 rounded hover:border-[var(--loss)] hover:text-[var(--loss)]"><Trash2 size={13}/></button>
      </div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="text-center border border-white/5 rounded py-2">
      <div className="text-[9px] font-mono uppercase tracking-widest text-[var(--text-muted)]">{label}</div>
      <div className="font-display text-lg text-white leading-none mt-1">{value}</div>
    </div>
  );
}

/** Full deployment detail page */
export function DeploymentDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [dep, setDep] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comparison, setComparison] = useState(null);
  const [compareBusy, setCompareBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/deployments/${id}`);
      setDep(data);
    } catch (e) {
      toast.error("Deployment not found");
      nav("/deployments");
    } finally { setLoading(false); }
  }, [id, nav]);
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  if (loading || !dep) {
    return (
      <div className="min-h-screen"><Navbar/>
        <p className="p-10 text-xs font-mono uppercase tracking-widest text-[var(--text-secondary)]">Loading…</p>
      </div>
    );
  }

  const s = dep.stats || {};
  const pnlPositive = (s.net_usd || 0) >= 0;

  return (
    <div className="min-h-screen"><Navbar/>
      <main className="max-w-[1200px] mx-auto px-6 py-10">
        <Link to="/deployments" className="text-xs font-mono text-[var(--text-secondary)] hover:text-white flex items-center gap-1 mb-4" data-testid="dep-back-link">
          <ArrowLeft size={12}/> All deployments
        </Link>
        <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="chip mb-2"><Activity size={12}/> LIVE DEPLOYMENT</div>
            <h1 className="font-display text-4xl text-white tracking-tight">{dep.label}</h1>
            <p className="text-xs font-mono text-[var(--text-secondary)] mt-1">
              {dep.symbol} · {dep.timeframe?.replace("PERIOD_","")} · {dep.provider} · {dep.connection_label}
            </p>
          </div>
          <button data-testid="dep-refresh-btn" onClick={load}
            className="btn-outline flex items-center gap-2 text-sm"><RefreshCw size={13}/> Refresh</button>
          <button data-testid="dep-compare-btn" disabled={compareBusy}
            onClick={async () => {
              setCompareBusy(true);
              try {
                const { data } = await api.get(`/deployments/${dep.deployment_id}/vs-backtest`);
                setComparison(data);
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Compare failed");
              } finally { setCompareBusy(false); }
            }}
            className="btn-outline flex items-center gap-2 text-sm">
            <GitCompare size={13}/> {compareBusy ? "Comparing…" : "Compare vs backtest"}
          </button>
          <button data-testid="dep-import-journal-btn" onClick={async () => {
            try {
              const { data } = await api.post(`/journal/import-deployment/${dep.deployment_id}`);
              toast.success(`Imported ${data.imported} trade(s) into your journal`);
            } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
          }} className="btn-outline flex items-center gap-2 text-sm">
            <TrendingUp size={13}/> Import to journal
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
          <BigStat label="Status" value={dep.status.toUpperCase()} testId="stat-status"/>
          <BigStat label="Trades" value={s.total ?? 0} testId="stat-total"/>
          <BigStat label="Open" value={s.open ?? 0} testId="stat-open"/>
          <BigStat label="Win rate" value={`${s.win_rate ?? 0}%`} testId="stat-winrate"/>
          <BigStat label="Net P&L" value={`${pnlPositive ? "+" : ""}$${(s.net_usd ?? 0).toFixed(2)}`}
            highlight={pnlPositive ? "profit" : "loss"} testId="stat-pnl"/>
        </div>

        {comparison && (
          <BacktestVsLivePanel data={comparison} onClose={()=>setComparison(null)}/>
        )}

        <DriftSettingsPanel dep={dep} onSaved={(updated)=>setDep(updated)}/>

        <div className="card-sharp p-5" data-testid="dep-trade-log">
          <h2 className="font-display text-lg text-white mb-3">Trade log</h2>
          {(dep.trades || []).length === 0 ? (
            <p className="text-xs font-mono text-[var(--text-muted)] italic">
              No trades yet. The runner ticks every 60 seconds; a trade will appear here when an entry signal fires.
            </p>
          ) : (
            <div className="overflow-auto">
              <table className="w-full text-xs font-mono">
                <thead className="text-[var(--text-muted)] uppercase tracking-widest">
                  <tr>
                    <th className="text-left py-1">Time</th>
                    <th>Symbol</th>
                    <th>Side</th>
                    <th>Entry</th>
                    <th>Exit</th>
                    <th>Status</th>
                    <th className="text-right">Pips</th>
                    <th className="text-right pr-2">USD</th>
                  </tr>
                </thead>
                <tbody>
                  {dep.trades.map((t, i) => (
                    <tr key={t.trade_id || `${t.entry_time}-${i}`} className="border-t border-white/5" data-testid={`trade-row-${i}`}>
                      <td className="py-2 text-[var(--text-secondary)]">{t.entry_time ? new Date(t.entry_time).toLocaleString() : "—"}</td>
                      <td className="text-white">{t.symbol}</td>
                      <td className={t.side === "buy" ? "text-[var(--profit)]" : "text-[var(--loss)]"}>{t.side}</td>
                      <td>{t.entry_price}</td>
                      <td>{t.exit_price ?? "—"}</td>
                      <td>{t.status}</td>
                      <td className={`text-right ${(t.pnl_pips ?? 0) >= 0 ? "text-[var(--profit)]" : "text-[var(--loss)]"}`}>
                        {t.pnl_pips ?? "—"}
                      </td>
                      <td className={`text-right pr-2 ${(t.pnl_usd ?? 0) >= 0 ? "text-[var(--profit)]" : "text-[var(--loss)]"}`}>
                        {t.pnl_usd != null ? `$${t.pnl_usd}` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function BigStat({ label, value, highlight, testId }) {
  const color = highlight === "profit" ? "text-[var(--profit)]" : highlight === "loss" ? "text-[var(--loss)]" : "text-white";
  return (
    <div className="border border-white/10 rounded p-3" data-testid={testId}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">{label}</div>
      <div className={`font-display text-2xl mt-1 ${color}`}>{value}</div>
    </div>
  );
}

